import { useState } from 'react';
import {
  downloadCustodianEvidenceExport,
  getCustodianEvidenceExport,
} from '../../api/custodianApi';
import { saveBlob } from '../custodian/saveBlob';
import { Button } from '../ui/Button';

const FORMATS = [
  { id: 'pdf', label: 'Export evidence (PDF)' },
  { id: 'xlsx', label: 'Excel' },
  { id: 'json', label: 'JSON' },
];

/**
 * Downloads the independently verifiable admission evidence (EV-14/15) as a
 * readable PDF statement (default), an Excel workbook, or the raw JSON a
 * verifier can re-hash. Shows the backend's verification result so a
 * tampered manifest is visible before anyone forwards the file.
 */
export function EvidenceExportButton({ packageId, fileName, variant = 'ghost', className = '' }) {
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  async function handleExport(format) {
    if (!packageId || busy) return;
    setBusy(format);
    setError('');
    try {
      if (format === 'json') {
        const payload = await getCustodianEvidenceExport(packageId);
        setResult({
          verified: Boolean(payload?.manifest_hash_verified),
          receiptsVerified: payload?.verification?.receipts_verified,
          receiptsTotal: payload?.verification?.receipts_total,
        });
        saveJson(payload, fileName || `evidence-${payload?.reference_number || packageId}.json`);
      } else {
        const download = await downloadCustodianEvidenceExport(packageId, format);
        setResult({
          verified: download.manifestHashVerified,
          receiptsVerified: download.receiptsVerified,
          receiptsTotal: download.receiptsTotal,
        });
        if (canSave()) saveBlob(download.blob, download.fileName);
      }
    } catch (requestError) {
      setResult(null);
      setError(requestError?.message || 'Unable to export evidence.');
    } finally {
      setBusy('');
    }
  }

  const verified = result ? result.verified : null;
  const receiptsVerified = result?.receiptsVerified;
  const receiptsTotal = result?.receiptsTotal;

  return (
    <div className={className}>
      <div className="flex flex-wrap items-center gap-2">
        {FORMATS.map((format, index) => (
          <Button
            key={format.id}
            type="button"
            variant={index === 0 ? variant : 'ghost'}
            disabled={Boolean(busy)}
            onClick={() => handleExport(format.id)}
            title={
              format.id === 'json'
                ? 'Machine-verifiable JSON (manifest, receipts, hashes)'
                : format.id === 'xlsx'
                  ? 'Workbook: summary, sealed files, receipts, transfers, manifest'
                  : 'Readable statement of what was admitted, under which mandate, by whom'
            }
          >
            {busy === format.id ? 'Exporting…' : format.label}
          </Button>
        ))}
      </div>
      {error ? (
        <p className="mt-1 text-[11px] text-red-600" role="alert">
          {error}
        </p>
      ) : null}
      {result && verified !== null ? (
        <p
          className={`mt-1 text-[11px] ${verified ? 'text-emerald-700' : 'text-red-600'}`}
          data-testid="evidence-export-result"
        >
          {verified ? 'Manifest hash verified' : 'Manifest hash did not verify'}
          {Number.isFinite(receiptsTotal)
            ? ` · ${receiptsVerified}/${receiptsTotal} receipts verified`
            : ''}
        </p>
      ) : null}
    </div>
  );
}

function canSave() {
  return typeof window !== 'undefined' && typeof URL?.createObjectURL === 'function';
}

function saveJson(payload, fileName) {
  if (!canSave()) return;
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  saveBlob(blob, fileName);
}
